import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "supplier-forecast-save-button" }
const _hoisted_2 = ["disabled", "aria-label"]
const _hoisted_3 = ["aria-label"]
const _hoisted_4 = ["aria-label"]
const _hoisted_5 = ["aria-label"]

import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Mdi from '@/corePagesModule/components/Mdi.vue';

/** 保存ボタンのUI状態 */
type SupplierForecastUiStatus = 'idle' | 'loading' | 'success' | 'error';

const SUCCESS_DURATION_DEFAULT = 1600;
const ERROR_DURATION_DEFAULT = 2000;

/**
 * 保存ボタンの表示状態を制御するコンポーネント
 * @property saveHandler - 保存処理を実行する非同期関数
 * @property disabled - ボタンを操作不可にするかどうか
 * @property ariaLabel - ボタン用のARIAラベル
 * @property loadingAriaLabel - ローディング表示時のARIAラベル
 * @property successAriaLabel - 成功表示時のARIAラベル
 * @property errorAriaLabel - エラー表示時のARIAラベル
 * @property successDuration - 成功表示からidleへ戻すまでの時間(ms)
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'SupplierForecastSaveButton',
  props: {
    saveHandler: { type: Function },
    disabled: { type: Boolean },
    ariaLabel: {},
    loadingAriaLabel: {},
    successAriaLabel: {},
    errorAriaLabel: {},
    successDuration: {},
    canSave: { type: Boolean }
  },
  emits: ["status-change", "success", "error"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

/**
 * クリックイベントを通知する
 */
const emit = __emit;

const currentStatus = ref<SupplierForecastUiStatus>('idle');
let successTimer: ReturnType<typeof setTimeout> | undefined;

const isDisabled = computed(() => {
  if (props.disabled) return true;
  if (currentStatus.value === 'loading' || currentStatus.value === 'success') return true;
  if (props.canSave !== undefined && !props.canSave) return true;
  return false;
});

/**
 * UI状態を更新してリスナーへ通知する
 * @param status - 設定する状態
 */
const setStatus = (status: SupplierForecastUiStatus) => {
  currentStatus.value = status;
  emit('status-change', status);
};

/**
 * idle 状態のときのみクリックを許可
 */
const handleClick = () => {
  if (isDisabled.value) return;
  runHandler();
};

/** 成功表示のタイマーを解除する */
const clearSuccessTimer = () => {
  if (successTimer) {
    clearTimeout(successTimer);
    successTimer = undefined;
  }
};

/** 保存処理を実行し、状態遷移を管理する */
const runHandler = async () => {
  if (!props.saveHandler) return;
  clearSuccessTimer();
  setStatus('loading');
  try {
    await props.saveHandler();
    setStatus('success');
    emit('success');
    const duration = props.successDuration ?? SUCCESS_DURATION_DEFAULT;
    successTimer = setTimeout(() => {
      setStatus('idle');
      successTimer = undefined;
    }, duration);
  } catch (error) {
    setStatus('error');
    emit('error', error);
    const duration = props.successDuration ?? ERROR_DURATION_DEFAULT;
    successTimer = setTimeout(() => {
      setStatus('idle');
      successTimer = undefined;
    }, duration);
  }
};

onBeforeUnmount(() => {
  clearSuccessTimer();
});

onMounted(() => {
  emit('status-change', currentStatus.value);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_Transition, {
      name: "supplier-forecast-fade",
      mode: "out-in"
    }, {
      default: _withCtx(() => [
        (currentStatus.value === 'idle')
          ? (_openBlock(), _createElementBlock("button", {
              key: "idle",
              type: "button",
              class: "supplier-forecast-save-button__icon-button",
              disabled: isDisabled.value,
              "aria-label": _ctx.ariaLabel,
              onClick: handleClick
            }, [
              _createVNode(Mdi, {
                class: "supplier-forecast-save-button__save-icon",
                icon: "save"
              })
            ], 8, _hoisted_2))
          : (currentStatus.value === 'loading')
            ? (_openBlock(), _createElementBlock("span", {
                key: "loading",
                class: "supplier-forecast-save-button__loading",
                role: "status",
                "aria-live": "polite",
                "aria-label": _ctx.loadingAriaLabel ?? _ctx.ariaLabel
              }, null, 8, _hoisted_3))
            : (currentStatus.value === 'success')
              ? (_openBlock(), _createElementBlock("span", {
                  key: "success",
                  class: "supplier-forecast-save-button__success",
                  role: "status",
                  "aria-live": "polite",
                  "aria-label": _ctx.successAriaLabel ?? _ctx.ariaLabel
                }, [
                  _createVNode(Mdi, {
                    class: "supplier-forecast-save-button__success-icon",
                    icon: "check_circle"
                  })
                ], 8, _hoisted_4))
              : (_openBlock(), _createElementBlock("button", {
                  key: "error",
                  type: "button",
                  class: "supplier-forecast-save-button__error",
                  "aria-label": _ctx.errorAriaLabel ?? _ctx.ariaLabel,
                  onClick: handleClick
                }, [
                  _createVNode(Mdi, {
                    class: "supplier-forecast-save-button__error-icon",
                    icon: "error"
                  })
                ], 8, _hoisted_5))
      ], undefined, true),
      _: 1
    })
  ]))
}
}

})